// tslint:disable-next-line:max-line-length
import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TekstResourcePipe } from 'src/app/core/pipes/tekstresource.pipe';
import tippy, { Instance } from 'tippy.js';

type Trigger = 'click' | 'mouseover' | 'enter' | 'focus';

@Directive({
  selector: '[bomPopover]',
})
export class PopoverDirective implements OnInit, OnChanges, OnDestroy {
  @Input()
  bomPopover: string | Observable<string>;

  @Input()
  triggers: Array<Trigger>;

  @Output()
  initialInvoke = new EventEmitter<any>();

  private defaultTriggers: Array<Trigger> = ['click', 'enter'];
  private tippyInstance: Instance;
  private content: string;
  private destroyed$ = new Subject<void>();
  private initialInvoked = false;

  constructor(private el: ElementRef, private tekstResourcePipe: TekstResourcePipe) {}

  @HostListener('click')
  onClick() {
    if (this.triggerEnabled('click')) {
      this.toggle();
    }
  }

  @HostListener('mouseover')
  onMouseOver() {
    if (this.triggerEnabled('mouseover')) {
      this.show();
    }
  }

  @HostListener('mouseout')
  onMouseOut() {
    if (this.triggerEnabled('mouseover')) {
      this.hide();
    }
  }

  @HostListener('focus')
  onFocus() {
    if (this.triggerEnabled('focus')) {
      // If both focus and click triggers are enabled we get a conflict. Focus makes the popver show
      // and afterwards click makes it toggle - hence making it disappear again.
      // Delaying the show on focus a bit avoids this
      if (this.triggerEnabled('click')) {
        setTimeout(() => this.show(), 100);
      } else {
        this.show();
      }
    }
  }

  @HostListener('keyup.enter')
  onKeyupEnter() {
    // NB: when applying the popover to buttons invoking it on enter does not work
    if (this.triggerEnabled('enter')) {
      this.toggle();
    }
  }

  @HostListener('blur')
  onBlur() {
    this.hide();
  }

  ngOnInit(): void {
    this.tippyInstance = tippy.one(this.el.nativeElement, {
      trigger: 'manual',
      arrow: true,
      content: this.tekstResourcePipe.transform('Henter'),
      zIndex: 9999,
      interactive: true,
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.bomPopover) {
      if (this.bomPopover instanceof Observable) {
        this.bomPopover
          .pipe(takeUntil(this.destroyed$))
          .subscribe(content => this.setContent(content));
      } else {
        this.setContent(this.bomPopover ? this.bomPopover.toString() : '');
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  private toggle() {
    if (this.tippyInstance.state.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  private show() {
    if (!this.initialInvoked) {
      this.initialInvoke.emit();
      this.initialInvoked = true;
    }

    if (this.content) {
      this.tippyInstance.setContent(this.content);
    }

    // For some reason the tippy popover wont show unless it's done asynchronously
    // https://github.com/atomiks/tippyjs/issues/133
    setTimeout(() => this.tippyInstance.show(), 0);
  }

  private hide() {
    this.tippyInstance.hide();
  }

  private setContent(content: string) {
    if (!content) {
      return;
    }

    this.content = content;

    if (this.tippyInstance) {
      this.tippyInstance.setContent(this.content);
    }
  }

  private triggerEnabled(trigger: Trigger): any {
    const triggers = this.triggers || this.defaultTriggers;
    return triggers.filter(t => t === trigger).length > 0;
  }
}
